import {
  Box,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { Avatar } from "./Avatar";

interface AvatarGroupProps {
  total?: number;
  max?: number;
  children: React.ReactNode;
  //   height and width of child Avatar components
  childWidthHeight?: string;
  //   child left margin to control overlap
  overlapMargin?: string;
  showExtrasNumberingAvatar?: boolean;
  // Controls stacking order: "first" = first avatar on top, "last" = last avatar on top
  stackOrder?: "first" | "last";
  // Style overrides for the "+N" counter circle (e.g. match bordered avatars).
  counterSx?: SxProps<Theme>;
}
export const AvatarGroup = ({
  total,
  max,
  children: childrenProp,
  childWidthHeight = "40px",
  overlapMargin = "-12px",
  showExtrasNumberingAvatar = true,
  stackOrder = "first",
  counterSx,
}: AvatarGroupProps) => {
  const theme = useTheme();
  const children = React.Children.toArray(childrenProp).filter((child) => {
    return React.isValidElement(child);
  });
  const slicedChildren = children.slice(0, max);
  const childCount = slicedChildren.length;

  return (
    // Children carry `position: relative` + a z-index to control their overlap.
    // Without a stacking context here those numbers land in the page's root
    // context and outrank anything that trusts a low z-index — the sticky table
    // header (z-index 2) and the bulk action bar both lost to a 3-avatar group.
    // `isolation` contains them without touching the group's own layout.
    <Stack direction="row" sx={{ isolation: "isolate" }}>
      {slicedChildren.map((child: any, index) => {
        // For "first" order: first item gets highest z-index
        // For "last" order: last item gets highest z-index (default)
        const zIndex = stackOrder === "first" ? childCount - index : index + 1;

        return React.cloneElement(child, {
          style: {
            marginLeft: overlapMargin,
            width: childWidthHeight,
            height: childWidthHeight,
            borderWidth: "2px",
            zIndex,
            position: "relative",
          },
        });
      })}
      {total || (max && max < children.length && showExtrasNumberingAvatar) ? (
        <Box
          sx={{
            marginLeft: overlapMargin,
            width: childWidthHeight,
            height: childWidthHeight,
            borderRadius: "100px",
            bgcolor: theme.palette.neutral["B-015"],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderWidth: "2px",
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.14)",
            cursor: "pointer",
            zIndex: childCount + 1,
            position: "relative",
            ...counterSx,
          }}
        >
          <Typography
            sx={{
              fontFamily: "SegoeBold",
              fontWeight: 700,
              fontSize: `calc(${childWidthHeight} * 0.38)`,
              textAlign: "center",
              color: theme.palette.lightBg.medium,
            }}
          >
            +{total ? total : max && children.length - max}
          </Typography>
        </Box>
      ) : (
        <></>
      )}
    </Stack>
  );
};
